// src/components/UserProfileView.jsx
import React, { useState, useEffect } from 'react';
import Post from './Post';

const UserProfileView = ({ user, onLogout, onClose, bluesky, bookmarkService }) => {
  const [profileData, setProfileData] = useState(null);
  const [userPosts, setUserPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
	const fetchProfileData = async () => {
	  setIsLoading(true);
	  try {
		if (!user?.did) {
		  throw new Error('No DID found');
		}

		// Fetch profile data
		const profileResponse = await bluesky.getProfile(user.did);
		if (profileResponse.success) {
		  setProfileData(profileResponse.data);
		} else {
		  setError('Failed to load profile data');
		}

		// Fetch posts
		const postsResponse = await bluesky.getUserPosts(user.did);
		if (postsResponse.success) {
		  const formattedPosts = postsResponse.data.map(post => ({
			post: {
			  uri: post.uri,
			  cid: post.cid,
			  author: profileResponse.data || {
				did: user.did,
				handle: user.handle,
				displayName: user.displayName,
				avatar: user.avatar
			  },
			  record: {
				text: post.record?.text || '',
				createdAt: post.record?.createdAt || new Date().toISOString(),
				embed: post.record?.embed || null
			  },
			  viewer: {
				like: post.viewer?.like,
				repost: post.viewer?.repost
			  },
			  likeCount: post.likeCount || 0,
			  repostCount: post.repostCount || 0,
			  replyCount: post.replyCount || 0,
			  indexedAt: post.indexedAt
			}
		  }));
		  setUserPosts(formattedPosts);
		} else {
		  setError(prev => prev ? `${prev}\nFailed to load posts` : 'Failed to load posts');
		}
	  } catch (err) {
		// Profile fetch error - displayed in UI
		setError(`Error loading profile: ${err.message}`);
	  } finally {
		setIsLoading(false);
	  }
	};

	fetchProfileData();
  }, [user?.did, bluesky]);

  const handleLike = async (uri, cid, isLiked) => {
	try {
	  const response = isLiked 
		? await bluesky.unlikePost(uri, cid)
		: await bluesky.likePost(uri, cid);
	  
	  if (response.success) {
		// Refresh posts after like/unlike
		const postsResponse = await bluesky.getUserPosts(user.did);
		if (postsResponse.success) {
		  setUserPosts(postsResponse.data);
		}
	  }
	} catch (err) {
	  // Like action failed - silent fail, UI will revert
	}
  };

  if (isLoading) {
	return <div className="loading">Loading profile...</div>;
  }

  const displayData = profileData || user;

  return (
	<div className="user-profile">
	  <div className="profile-header">
		{displayData?.banner && (
		  <div 
			className="profile-banner"
			style={{ 
			  backgroundImage: `url(${displayData.banner})`,
			  backgroundColor: '#f0f0f0'
			}}
		  />
		)}
		<div className="profile-info">
		  {displayData?.avatar && (
			<img 
			  src={displayData.avatar} 
			  alt={`${displayData.displayName || displayData.handle}'s avatar`}
			  className="profile-avatar"
			/>
		  )}
		  <div className="profile-details">
			<h2>{displayData?.displayName || displayData?.handle}</h2>
			<p className="handle">@{displayData?.handle}</p>
			
			<div className="stats">
			  <span>{displayData?.followersCount || 0} followers</span>
			  <span>{displayData?.followsCount || 0} following</span>
			  <span>{displayData?.postsCount || 0} posts</span>
			</div>

			{displayData?.description && (
			  <div className="profile-bio">
				<p>{displayData.description}</p>
			  </div>
			)}
		  </div>
		</div>
	  </div>

	  <div className="profile-actions">
		<button
		  onClick={() => window.open(`https://bsky.app/profile/${displayData?.handle}`, '_blank')}
		  className="button button-secondary"
		>
		  View on Bluesky
		</button>
		<button
		  onClick={onLogout}
		  className="button button-secondary logout-button"
		>
		  Log out
		</button>
	  </div>

	  <div className="profile-content">
		{error && <div className="error-message">{error}</div>}
		
		<h3>Posts</h3>
		
		{userPosts.length === 0 ? (
		  <div className="no-posts">No posts yet</div>
		) : (
		  <div className="posts-grid">
			{userPosts.map(postData => (
			  <Post
				key={postData.post.cid}
				post={postData}
				bluesky={bluesky}
				isLoggedIn={true}
				onLoginRequired={() => {}}
				bookmarkService={bookmarkService}
				onLike={handleLike}
			  />
			))}
		  </div>
		)}
	  </div>
	</div>
  );
};

export default UserProfileView;